import { Box, HStack, Icon } from "@chakra-ui/react";
import { FiAlertTriangle, FiCalendar } from "react-icons/fi";
import { LuCalendarPlus } from "react-icons/lu";
import { IconType } from "react-icons";
import CDS from "design-system";
import IconSquare from "components/IconSquare";
import { dateInFormat } from "helpers/time";
import { Meeting } from "features/Resources/Meeting/models";
import { PrefixId } from "features/Resources/Shared/models";
import { SchedulingButton } from "features/Resources/Meeting/Components/Button/Scheduling";
import SkipMeetingButton from "features/Resources/Meeting/Components/Button/SkipMeetingButton";
import { dayOffsetFromStart } from "features/Resources/Meeting/helpers";

type SoftTone = "red" | "blue";
const TONES: Record<SoftTone, { bg: string; color: string; hover: string }> = {
  red: { bg: "red.50", color: "red.600", hover: "red.100" },
  blue: { bg: "blue.50", color: "blue.600", hover: "blue.100" },
};

export function SoftButton({
  label,
  icon,
  tone,
  onClick,
}: {
  label: string;
  icon?: IconType;
  tone: SoftTone;
  onClick: () => void;
}) {
  const t = TONES[tone];
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="inline-flex"
      alignItems="center"
      gap={1.5}
      px={3}
      py={1.5}
      borderRadius="md"
      bg={t.bg}
      _hover={{ bg: t.hover }}
      flexShrink={0}
    >
      {icon && <Icon as={icon} boxSize={3.5} color={t.color} />}
      <CDS.Text12 fontWeight="semibold" color={t.color}>
        {label}
      </CDS.Text12>
    </Box>
  );
}

interface ScheduleActionRowProps {
  meeting: Meeting;
  variant: "fix" | "schedule";
  onExpand: (prefixId: PrefixId) => void;
  /** For "fix": the red reason line. */
  reason?: string;
  /** For "schedule": journey start date, for the "day N" deadline label. */
  startDate?: Date | string;
}

export default function ScheduleActionRow({ meeting, variant, onExpand, reason, startDate }: ScheduleActionRowProps) {
  const isFix = variant === "fix";

  const subtitle = isFix ? reason : `No time set · ${meeting.durationMinutes} min`;

  const meta = isFix
    ? dateInFormat(meeting.scheduledAt, "MMM D · h:mm A", meeting.timezone)
    : `~ ${dateInFormat(meeting.proposedAt, "MMM D", meeting.timezone)}${
        startDate && meeting.proposedAt ? ` · day ${dayOffsetFromStart(meeting.proposedAt, startDate)}` : ""
      }`;

  return (
    <HStack spacing={3} py={2.5} align="center" w="100%">
      <IconSquare
        icon={isFix ? FiAlertTriangle : FiCalendar}
        bg={isFix ? "red.50" : "orange.50"}
        color={isFix ? "red.500" : "orange.500"}
      />
      <Box flex={1} minW={0}>
        <Box as="button" type="button" textAlign="left" onClick={() => onExpand(meeting.prefixId)}>
          <CDS.Text14 fontWeight="semibold" wordBreak="break-word">
            {meeting.name}
          </CDS.Text14>
        </Box>
        {subtitle && <CDS.Text12 color={isFix ? "red.500" : "orange.600"}>{subtitle}</CDS.Text12>}
      </Box>
      <CDS.Text12 color="fg.muted" flexShrink={0} whiteSpace="nowrap">
        {meta}
      </CDS.Text12>
      <HStack spacing={2} flexShrink={0}>
        {!isFix && (
          <SkipMeetingButton meeting={meeting}>
            {(title) => (
              <Box as="button" type="button" px={2} py={1.5} borderRadius="md" _hover={{ bg: "gray.100" }}>
                <CDS.Text12 fontWeight="medium" color="fg.muted">
                  {title}
                </CDS.Text12>
              </Box>
            )}
          </SkipMeetingButton>
        )}
        <SchedulingButton
          meeting={meeting}
          renderButton={(title, onOpen) => (
            <SoftButton
              label={title}
              icon={isFix ? undefined : LuCalendarPlus}
              tone={isFix ? "red" : "blue"}
              onClick={onOpen}
            />
          )}
        />
      </HStack>
    </HStack>
  );
}
